/**
 * Tasks inbox (development plan §18.6, §19.4; under Deliver): To do · In progress · Needs you · Done, filtered by
 * project, assignee and type. A task opens to its brief, a link to its target, comments and history. Tasks come from
 * pipeline triggers or from "Create task…" anywhere in the app; people are the only assignees until agents (N2).
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { TASK_TYPES, type TaskType } from '@castfeeds/domain';
import { useT } from '@/i18n';
import { Avatar, Empty, ErrorBox, Ltr, Modal, QueryView, StatusPill } from '@/components/ui';
import { CreateTaskButton, TaskStatusPill, useTaskPeople } from '@/components/tasks';
import { api } from '@/lib/api/endpoints';
import { useProjects, useTask, useTasks, useWsMutation } from '@/lib/api/hooks';
import { can } from '@/lib/constants';
import { ago, dateTime, relativeDay, timecode } from '@/lib/format';
import { logInfo } from '@/lib/log';
import { columnOf, TASK_COLUMNS, targetLink, taskTitle, type TaskColumn } from '@/lib/tasks';
import { useMembership } from '@/store/session';
import type { Task, TaskDetail, TaskEvent, TaskPatchBody } from '@/lib/api/types';

const PRIO_TONE: Record<string, string> = { urgent: 'changes', high: 'review', normal: 'draft', low: 'draft' };
const typeKey = (type: string) => `tkType_${type.replace('.', '_')}`;

function TaskCard({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const { t, td, locale } = useT();
  return (
    <li>
      <button type="button" className="clickable card" onClick={onOpen} style={{ display: 'grid', gap: 6, padding: 12, width: '100%', textAlign: 'start' }}>
        <span style={{ fontSize: 13, fontWeight: 500, lineHeight: '18px' }}>{taskTitle({ td }, task)}</span>
        <span className="muted small ellipsis">{td(typeKey(task.type), task.type)}{task.project ? ` · ${task.project.title}` : ''}</span>
        <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {task.priority === 'urgent' || task.priority === 'high' ? <StatusPill status={PRIO_TONE[task.priority]} label={td(`tkPrio_${task.priority}`, task.priority)} /> : null}
          {task.status === 'needs_you' && task.waiting_on ? <span className="small" style={{ color: 'var(--accent-text)' }}>{t('tkWaitingOn', { name: task.waiting_on.name })}</span> : null}
          {task.due_at ? <span className="muted small">{t('tkDueOn', { when: relativeDay(locale, task.due_at, { today: t('today'), tomorrow: t('tomorrow') }) })}</span> : null}
          <span className="push">{task.assignee ? <Avatar name={task.assignee.name} size={22} /> : <span className="muted small">{t('tkUnassigned')}</span>}</span>
        </span>
      </button>
    </li>
  );
}

function Board({ open, done, onOpen }: { open: Task[]; done: Task[]; onOpen: (id: string) => void }) {
  const { t } = useT();
  const by = useMemo(() => {
    const m: Record<TaskColumn, Task[]> = { todo: [], in_progress: [], needs_you: [], done };
    for (const x of open) m[columnOf(x.status)].push(x);
    return m;
  }, [open, done]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, alignItems: 'start' }}>
      {TASK_COLUMNS.map((c) => (
        <section key={c} aria-labelledby={`col-${c}`} className="stack" style={{ gap: 8, background: 'var(--raise)', borderRadius: 8, padding: 10 }}>
          <h2 id={`col-${c}`} className="row" style={{ fontSize: 13, fontWeight: 600, margin: 0, gap: 6 }}>
            {t(`tkCol_${c}`)}
            <span className="badge" style={{ background: c === 'needs_you' && by[c].length ? 'var(--accent)' : 'var(--card)', color: c === 'needs_you' && by[c].length ? 'var(--on-accent)' : 'var(--text)' }}>{by[c].length}</span>
          </h2>
          {by[c].length === 0 ? <span className="muted small" style={{ padding: '6px 2px' }}>{t('tkEmptyCol')}</span> : null}
          <ul aria-label={t(`tkCol_${c}`)} className="stack" style={{ gap: 8, listStyle: 'none', margin: 0, padding: 0 }}>
            {by[c].map((x) => <TaskCard key={x.id} task={x} onOpen={() => onOpen(x.id)} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}

function eventText(i: ReturnType<typeof useT>, e: TaskEvent, nameOf: (id: unknown) => string): string {
  const actor = e.actor?.name || i.t('actSystem');
  const b = e.body;
  switch (e.kind) {
    case 'created':
      return b.by === 'trigger' ? i.t('tkEv_created_trigger') : i.t('tkEv_created', { actor });
    case 'status':
      if (b.reason === 'no_longer_with_customer' || b.reason === 'post_no_longer_held') return i.t('tkEv_resolved');
      return i.t('tkEv_status', { actor, status: i.td(`tkStatus_${String(b.to)}`, String(b.to)) });
    case 'assign':
      return b.to ? i.t('tkEv_assign', { actor, to: nameOf(b.to) }) : i.t('tkEv_unassign', { actor });
    case 'trigger':
      return i.t('tkEv_trigger');
    case 'edit':
      return i.t('tkEv_edit', { actor });
    default:
      return actor;
  }
}

/** What the trigger saw: reasons, change requests, the customer's comments. */
function TriggerDetails({ task }: { task: TaskDetail }) {
  const { td } = useT();
  const inp = task.inputs;
  const reasons = Array.isArray(inp.reasons) ? (inp.reasons as string[]) : [];
  const notes = Array.isArray(inp.notes) ? (inp.notes as { by: string; note: string }[]) : [];
  const comments = Array.isArray(inp.comments) ? (inp.comments as { by: string; body: string; t_ms: number | null }[]) : [];
  if (!reasons.length && !notes.length && !comments.length) return null;
  return (
    <ul className="stack" style={{ gap: 6, margin: 0, paddingInlineStart: 18, fontSize: 13 }}>
      {reasons.map((r) => <li key={r}>{td(`trgReason_${r}`, r)}{r === 'manual_clap' && Array.isArray(inp.clap_sources) ? `: ${(inp.clap_sources as string[]).join(', ')}` : ''}</li>)}
      {notes.map((n, k) => <li key={`n${k}`}><strong>{n.by}</strong>{n.note ? `: “${n.note}”` : ''}</li>)}
      {comments.map((c, k) => (
        <li key={`c${k}`}>
          <strong>{c.by}</strong>
          {c.t_ms !== null && c.t_ms !== undefined ? <> · <Ltr className="mono">{timecode(c.t_ms)}</Ltr></> : null}: “{c.body}”
        </li>
      ))}
    </ul>
  );
}

function TaskDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const i = useT();
  const { t, td, locale } = i;
  const q = useTask(id);
  const { people, me } = useTaskPeople();
  const [comment, setComment] = useState('');
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [who, setWho] = useState('');
  const patch = useWsMutation((b: TaskPatchBody) => api.tasks.patch(id, b), { onSuccess: (d, b) => logInfo('tasks', `task=${id} patched ${JSON.stringify(b)} → ${d.status}`) });
  const post = useWsMutation((body: string) => api.tasks.comment(id, body), { onSuccess: () => setComment('') });
  const nameOf = (mid: unknown) => people.find((p) => p.id === mid)?.name ?? (q.data?.assignee && q.data.assignee.member_id === mid ? q.data.assignee.name : '');
  const submitComment = (e: FormEvent) => {
    e.preventDefault();
    if (comment.trim()) post.mutate(comment.trim());
  };
  const submitAsk = (e: FormEvent) => {
    e.preventDefault();
    patch.mutate({ status: 'needs_you', note: note.trim() || undefined, waiting_on_member_id: who || undefined }, { onSuccess: () => setAsking(false) });
  };
  return (
    <QueryView query={q} loading={null}>
      {(task) => {
        const link = targetLink(task);
        const open = task.status === 'todo' || task.status === 'in_progress' || task.status === 'needs_you';
        return (
          <Modal title={taskTitle({ td }, task)} onClose={onClose} wide>
            <div className="stack" style={{ gap: 14 }}>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <TaskStatusPill status={task.status} />
                <span className="tag">{td(typeKey(task.type), task.type)}</span>
                <StatusPill status={PRIO_TONE[task.priority]} label={td(`tkPrio_${task.priority}`, task.priority)} />
                {task.due_at ? <span className="muted small">{t('tkDueOn', { when: dateTime(locale, task.due_at) })}</span> : null}
                <span className="muted small push">{task.created_by ? t('tkFrom', { name: task.created_by.name }) : t('tkFromPipeline')} · {ago(locale, task.created_at)}</span>
              </div>
              {task.status === 'needs_you' && task.waiting_on ? <div className="alert alert-warn">{t('tkWaitingOn', { name: task.waiting_on.name })}</div> : null}
              {task.brief ? <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '21px' }}>{task.brief}</p> : null}
              <TriggerDetails task={task} />
              {link ? (
                <div className="row" style={{ gap: 8 }}>
                  <span className="muted small">{[task.project?.title, task.episode ? `${task.episode.number ? `${task.episode.number} · ` : ''}${task.episode.title}` : null, task.edit?.title].filter(Boolean).join(' › ')}</span>
                  <Link to={link.to} className="btn btn-sm push" onClick={onClose}>
                    <ExternalLink size={13} aria-hidden />
                    {link.kind === 'video' ? t('tkOpenVideo') : link.kind === 'episode' ? t('tkOpenEpisode') : t('tkOpenProject')}
                  </Link>
                </div>
              ) : null}

              <div className="row" style={{ gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                <label className="row" style={{ gap: 6, fontSize: 13 }}>
                  <span className="muted">{t('tkAssignee')}</span>
                  <select
                    className="select"
                    style={{ width: 'auto' }}
                    aria-label={t('tkAssignee')}
                    disabled={!task.can.assign || patch.isPending}
                    value={task.assignee?.member_id ?? ''}
                    onChange={(e) => patch.mutate({ assignee_member_id: e.target.value || null })}
                  >
                    <option value="">{t('tkUnassigned')}</option>
                    {(task.can.assign ? people : people.filter((p) => p.id === task.assignee?.member_id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </label>
                {task.can.take && me ? <button type="button" className="btn btn-sm" onClick={() => patch.mutate({ assignee_member_id: me.id, status: task.status === 'todo' ? 'in_progress' : undefined })}>{t('tkTake')}</button> : null}
                {!task.can.assign && task.assignee?.member_id === me?.id && open ? <button type="button" className="btn btn-sm" onClick={() => patch.mutate({ assignee_member_id: null })}>{t('tkHandBack')}</button> : null}
                {task.can.status ? (
                  <span className="row push" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {task.status === 'todo' || task.status === 'needs_you' ? <button type="button" className="btn btn-sm" onClick={() => patch.mutate({ status: 'in_progress' })}>{t('tkStart')}</button> : null}
                    {open && task.status !== 'needs_you' ? <button type="button" className="btn btn-sm" onClick={() => setAsking((x) => !x)}>{t('tkAskDecision')}</button> : null}
                    {open ? <button type="button" className="btn btn-sm btn-primary" onClick={() => patch.mutate({ status: 'done' })}>{t('tkMarkDone')}</button> : null}
                    {!open ? <button type="button" className="btn btn-sm" onClick={() => patch.mutate({ status: 'todo' })}>{t('tkReopen')}</button> : null}
                    {open && task.can.assign ? <button type="button" className="btn btn-sm btn-ghost" onClick={() => patch.mutate({ status: 'cancelled' })}>{t('tkCancelTask')}</button> : null}
                  </span>
                ) : null}
              </div>
              {asking ? (
                <form className="stack card card-pad" style={{ gap: 8 }} onSubmit={submitAsk}>
                  <label className="stack" style={{ gap: 4, fontSize: 13 }}>
                    <span>{t('tkNeedsNote')}</span>
                    <textarea className="textarea" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                  <label className="stack" style={{ gap: 4, fontSize: 13 }}>
                    <span>{t('tkNeedsWho')}</span>
                    <select className="select" value={who} onChange={(e) => setWho(e.target.value)}>
                      <option value="">{t('tkNeedsDefault')}</option>
                      {people.filter((p) => p.id !== me?.id).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </label>
                  <button type="submit" className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={patch.isPending}>{t('tkSend')}</button>
                </form>
              ) : null}
              {patch.error ? <ErrorBox error={patch.error} /> : null}

              <section aria-label={t('tkHistory')} className="stack" style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                <h3 className="h2" style={{ fontSize: 14 }}>{t('tkHistory')}</h3>
                <ol className="stack" style={{ gap: 8, listStyle: 'none', margin: 0, padding: 0 }}>
                  {task.events.map((e) => (
                    <li key={e.id} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) auto', gap: 8, alignItems: 'start', fontSize: 13 }}>
                      <Avatar name={e.actor?.name || 'Castfeeds'} size={24} colour={e.actor ? undefined : 'var(--raise)'} />
                      {e.kind === 'comment' ? (
                        <span className="stack" style={{ gap: 2 }}><strong>{e.actor?.name}</strong><span style={{ whiteSpace: 'pre-wrap' }}>{String(e.body.text ?? '')}</span></span>
                      ) : (
                        <span className="muted">{eventText(i, e, nameOf)}{typeof e.body.note === 'string' && e.body.note ? `: “${e.body.note}”` : ''}</span>
                      )}
                      <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{ago(locale, e.at)}</span>
                    </li>
                  ))}
                </ol>
                {task.can.comment ? (
                  <form className="row" style={{ gap: 8 }} onSubmit={submitComment}>
                    <input className="input" aria-label={t('tkCommentPh')} placeholder={t('tkCommentPh')} maxLength={4000} value={comment} onChange={(e) => setComment(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!comment.trim() || post.isPending}>{t('post')}</button>
                  </form>
                ) : null}
                {post.error ? <ErrorBox error={post.error} /> : null}
              </section>
            </div>
          </Modal>
        );
      }}
    </QueryView>
  );
}

export function TasksPage() {
  const { t, td } = useT();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const membership = useMembership();
  const { people } = useTaskPeople();
  const projects = useProjects();
  const filter = { project_id: params.get('project') ?? undefined, assignee: params.get('assignee') ?? undefined, type: (params.get('type') as TaskType | null) ?? undefined };
  const allowed = can(membership?.role, 'tasks.view');
  const open = useTasks({ ...filter, status: 'open', limit: 200 }, allowed);
  const done = useTasks({ ...filter, status: 'closed', limit: 30 }, allowed);
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const openTask = (id: string) => navigate(`/tasks/${id}${location.search}`);
  if (!allowed) return <div className="page"><div className="alert alert-error">{t('tkNotFound')}</div></div>;
  return (
    <div className="page" style={{ maxWidth: 1400 }}>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div className="stack">
          <h1 className="h1">{t('nTasks')}</h1>
          <span className="muted">{t('tkSub')}</span>
        </div>
        <CreateTaskButton target={filter.project_id ? { project_id: filter.project_id } : {}} className="btn btn-primary" />
      </div>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <select className="select" style={{ width: 'auto' }} aria-label={t('tkFilterProject')} value={filter.project_id ?? ''} onChange={(e) => set('project', e.target.value)}>
          <option value="">{t('tkAllProjects')}</option>
          {(projects.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select className="select" style={{ width: 'auto' }} aria-label={t('tkFilterAssignee')} value={filter.assignee ?? ''} onChange={(e) => set('assignee', e.target.value)}>
          <option value="">{t('tkAnyone')}</option>
          <option value="me">{t('tkMe')}</option>
          <option value="unassigned">{t('tkUnassigned')}</option>
          {people.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select className="select" style={{ width: 'auto' }} aria-label={t('tkFilterType')} value={filter.type ?? ''} onChange={(e) => set('type', e.target.value)}>
          <option value="">{t('tkAllTypes')}</option>
          {TASK_TYPES.map((x) => <option key={x} value={x}>{td(typeKey(x), x)}</option>)}
        </select>
      </div>
      <QueryView query={open}>
        {(o) =>
          o.items.length === 0 && (done.data?.items.length ?? 0) === 0 && !filter.project_id && !filter.assignee && !filter.type ? (
            <div className="card"><Empty>{t('tkEmpty')}</Empty></div>
          ) : (
            <Board open={o.items} done={done.data?.items ?? []} onOpen={openTask} />
          )
        }
      </QueryView>
      {done.error ? <ErrorBox error={done.error} /> : null}
      {taskId ? <TaskDialog id={taskId} onClose={() => navigate(`/tasks${location.search}`)} /> : null}
    </div>
  );
}
